﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { FETCH_DEALERS_SUCCESS, INITIAL_DEALERS_REQUEST, INITIAL_DEALER_REQUEST, FINAL_DEALER_REQUEST } from "@/redux/types/maintainers/dealers.type";

import Head from "@/components/Content/Head";
import classes from "../maintainers.module.scss";

import api from "@/services/api.service";

import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import DealersTableHead from "./DealersTableHead";
import DealersTableBody from "./DealersTableBody";
import DealersForm from "./DealersForm/DealersForm";
import { toast } from "@/services/Utils";
import DealersActions from "./DealersActions";

const Dealers = () => {
	const dispatch = useDispatch();
	const { addToast } = useToasts();
	const navigate = useNavigate();

	const { search } = useSelector((state) => state.auth.filter);
	const { isFetching, quantity, last_page, from, to } = useSelector((state) => state.maintainers_dealers);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(10);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("desc");
	const [loaded, setLoaded] = useState(false);

	const [showDelete, setShowDelete] = useState(false);
	const [showGroup, setShowGroup] = useState(false);

	const [group, setGroup] = useState([]);

	useEffect(() => {
		dispatch({ type: INITIAL_DEALERS_REQUEST });
		api.get("/admin/dealers", {
			page,
			paginate,
			order,
			orderType,
			search,
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [search, order, orderType, paginate, page]);

	const updateData = (response) => {
		if (Object.prototype.hasOwnProperty.call(response, "dealers")) {
			const { dealers } = response;
			dispatch({
				type: FETCH_DEALERS_SUCCESS,
				payload: {
					dealers: dealers.data,
					last_page: dealers.last_page,
					from: dealers.from,
					to: dealers.to,
					quantity: dealers.total,
				},
			});
			setLoaded(true);
		} else {
			setLoaded(true);
		}
	};

	const handleDelete = (item) => {
		setSelected(item.dealer_id);
		setShowDelete(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_DEALERS_REQUEST });
		api.post("/admin/dealers/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
		}).then((response) => {
			setSelected("");
			toast(addToast, "Distribuidor eliminado correctamente.", "success");
			updateData(response);
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/dealers/save", {
			...form.form,
			page,
			paginate,
			order,
			orderType,
			search,
		}).then((response) => {
			if (Object.prototype.hasOwnProperty.call(response, "dealers")) {
				updateData(response);
				modalFetching(false);
				afterSave();
			} else {
				modalFetching(false);
			}
		});
	};

	const handleGroup = () => {
		setShowGroup(true);
	};

	const confirmGroup = () => {
		setShowGroup(false);

		dispatch({ type: INITIAL_DEALER_REQUEST });
		api.post("/admin/dealers/join", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			dealers: group,
		}).then((response) => {
			const base_id = group[0].dealer_id;

			setGroup([]);
			if (Object.prototype.hasOwnProperty.call(response, "dealers")) {
				toast(addToast, "Distribuidores agrupados correctamente.", "success");
				updateData(response);
				navigate("/mantenedores/distribuidores/editar/" + base_id);
			} else {
				dispatch({ type: FINAL_DEALER_REQUEST });
			}
		});
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head
				title="Distribuidores"
				button={true}
				button_text={"Nuevo Distribuidor"}
				action={() => navigate("/mantenedores/distribuidores/crear")}
				back_url={"/mantenedores"}
			/>

			<Row className={` ${classes.main_row}`}>
				<Col md={12} className={classes.main_col}>
					<Row className={`${classes.header} ${group.length > 0 && classes.have_action}`}>
						{group.length > 0 && <DealersActions handleGroup={handleGroup} />}
					</Row>

					<Row className={classes.table}>
						<div className={classes.table_div}>
							<Table className={classes.table_desktop}>
								<DealersTableHead order={order} setOrder={setOrder} orderType={orderType} setOrderType={setOrderType} />

								<DealersTableBody handleDelete={handleDelete} group={group} setGroup={setGroup} />
							</Table>
						</div>
					</Row>

					<BasePagination
						page={page}
						paginate={paginate}
						last_page={last_page}
						quantity={quantity}
						from={from}
						to={to}
						setPage={setPage}
						setPaginate={setPaginate}
					/>
				</Col>
			</Row>

			{loaded && (
				<>
					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />

					<BasicConfirm
						showConfirm={showGroup}
						setShowConfirm={setShowGroup}
						handleAction={confirmGroup}
						message={"¿Está seguro de que desea agrupar estos proveedores?"}
						subMessage={"Al realizar esta acción se abrirá un formulario para completar la información del proveedor unificado."}
						showValidation={true}
						textValidation={"juntar proveedores"}
					/>

					<DealersForm confirmSave={confirmSave} />
				</>
			)}
		</>
	);
};

export default Dealers;
